import { useState } from 'react';
import type { QuotationResource } from '@neuroedge/shared';
import { Button, Modal, useToast } from '../../components/ui';
import { useUpdateQuotationItems } from '../../api/quotations';
import { QuotationItemsEditor } from './QuotationItemsEditor';
import { rowsFromQuotation, toItems, validateRows, type EditorRow } from './quotation-editor-model';

/**
 * Edit a quotation's lines (product, qty, price, discount; add/remove).
 * Saving puts it back to "Generated" so the updated version can be sent -
 * the customer only sees the change once it's sent again.
 */
export function EditQuotationModal({
  quotation,
  onClose,
}: {
  quotation: QuotationResource;
  onClose: () => void;
}) {
  const toast = useToast();
  const save = useUpdateQuotationItems(quotation.id);
  const [rows, setRows] = useState<EditorRow[]>(() => rowsFromQuotation(quotation));
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function onSave() {
    const found = validateRows(rows);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error('Please fix the highlighted fields.');
      return;
    }
    try {
      await save.mutateAsync({ items: toItems(rows) });
      toast.success(
        quotation.status === 'SENT'
          ? 'Quotation updated - send it again so the customer gets the new version.'
          : 'Quotation updated.',
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the quotation.');
    }
  }

  return (
    <Modal title={`Edit quotation ${quotation.reference}`} onClose={onClose} wide maxWidth={980}>
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
          {quotation.contactDisplayName ?? 'Contact'}
          {quotation.contactPhone ? ` · ${quotation.contactPhone}` : ''}
          {quotation.status === 'SENT'
            ? ' - already sent; after saving, send it again to share the update.'
            : ''}
        </p>
        <QuotationItemsEditor
          rows={rows}
          onChange={(next) => {
            setRows(next);
            if (Object.keys(errors).length > 0) setErrors({});
          }}
          currency={quotation.currency}
          errors={errors}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void onSave()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
